/**
 * "Your plan, in plain words" — the Downsizer module's interpretation layer, the same guided-UX
 * pattern as the Aged Care module (owner direction, 2026-08-25: port the design principles across
 * every module). Sentences for people who don't read the input cards above, then the pension and
 * cashflow movements as ShiftRows with driver-named whys. Content transposed from
 * DownsizerInsightPanel's narratives (the downsizing trap, the buy-up story, the self-funded
 * case), so the closable panel and this always-visible card tell one story.
 *
 * CALCULATION DISCIPLINE — every figure is quoted from `panelModel`, the SAME object that feeds
 * the Financial Impact panel and the insight panel, for whichever view is live (manual settings or
 * the selected Suggestion Engine strategy). Nothing is re-derived, so this card cannot disagree
 * with the panels beside it.
 */
import React from 'react';
import { formatCurrency } from '../../utils/index.ts';
import { JargonTerm } from '../../components/narrative/index.tsx';
import { PlainWordsCard, ShiftRow, WhatMovesIt, type EstateDriver } from '../../components/narrative/PlainWords.tsx';
import { PlanPiecesCard, type PlanPiece } from '../../components/narrative/PlanPieces.tsx';
import { GIFTING_LIMIT_ANNUAL } from '../../constants/index.ts';
import type { DownsizerInsightFinancials } from './downsizerTypes.ts';

interface RecapModel {
    name?: string | null;
    before: { socialSecurity?: number; cashFlow?: number };
    hasDownsize: boolean;
    salePrice: number;
    sellingCosts: number;
    newHomePrice: number;
    freedCapital: number;
    buyUpShortfall: number;
    sellAndRent: boolean;
    downsizerContribution: number;
    insightFinancials: DownsizerInsightFinancials | null;
}

interface DownsizerPlanRecapProps {
    model: RecapModel;
    /** Manual-mode gift from the proceeds; not part of an SE strategy's view. */
    giftingAmount?: number;
    firstName?: string;
    /**
     * What the estate would be at the horizon if the client simply DID NOT downsize — the only fair
     * benchmark for "does this move leave more or less behind", and the same figure the Financial
     * Impact panel's estate tile uses as its "before". Null when it could not be computed, in which
     * case the row is omitted rather than compared against something else.
     *
     * Added 2026-08-27: the recap explained the pension and the cashflow and then stopped, while the
     * panel beside it reported an estate nearly a million dollars behind. For most families the
     * estate IS the decision, and it was the one movement with no reason attached.
     */
    baselineLegacy?: number | null;
    legacyYears?: number;
}

const DownsizerPlanRecap = ({ model, giftingAmount = 0, firstName, baselineLegacy = null, legacyYears = 5 }: DownsizerPlanRecapProps) => {
    const fin = model.insightFinancials;
    if (!model.hasDownsize || !fin) return null;

    const name = firstName || 'you';
    const pensionBefore = fin.agePensionBefore ?? 0;
    const pensionAfter = fin.agePensionAfter ?? 0;
    const cashflowBefore = model.before?.cashFlow ?? 0;
    const cashflowAfter = fin.cashflowAfter ?? 0;
    const pensionDelta = Math.round(pensionAfter) - Math.round(pensionBefore);
    const isBuyUp = model.buyUpShortfall > 0;
    const selfFunded = pensionBefore <= 0;
    const isSeView = !!model.name;
    const gift = isSeView ? 0 : giftingAmount;
    const giftExcess = Math.max(0, gift - GIFTING_LIMIT_ANNUAL);

    /*
     * Driver-named whys, never generic — the aged care module's rule. Each explanation quotes the
     * same figures as the sentences above it and names what actually moved this client's pension.
     */
    const pensionWhy = isBuyUp
        ? pensionDelta > 0
            ? `The new home costs more than the old one sold for, so ${formatCurrency(model.buyUpShortfall)} of savings and investments Centrelink was counting moved into an exempt home. Less counted, more pension. The trade-off: those assets were earning income, and the money is no longer easy to reach.`
            : `Moving ${formatCurrency(model.buyUpShortfall)} into the exempt home takes it out of the assets test, but other effects (like the income those assets were earning) offset it here — the Financial Impact panel shows the same before-and-after.`
        : model.sellAndRent
          ? `Renting makes ${name === 'you' ? 'you' : name} a non-homeowner: the whole ${formatCurrency(model.salePrice)} of home equity becomes money Centrelink counts and deems, though non-homeowners get a higher assets-test threshold and possibly Rent Assistance. The net of those is what the figures show.`
          : selfFunded
            ? `No Age Pension is being received today, so the usual downsizing trap — freed-up cash reducing the pension — doesn't bite. The planning is in where the proceeds go (super, an income stream, the downsizer contribution).`
            : pensionDelta < 0
              ? `The home didn't count for the pension; the ${formatCurrency(model.freedCapital)} it freed up does — Centrelink counts it in the assets test and deems income on it. If the move is really about a more suitable home, buying one of similar value keeps the pension unchanged.`
              : pensionDelta > 0
                ? `Money moved from counted assets into exempt ones, so Centrelink counts less than before.`
                : `Where ${name === 'you' ? 'you sit' : `${name} sits`} on the means test, the freed money doesn't shift the pension. A similar-value home would leave it unchanged too.`;

    const cashflowDrivers: string[] = [];
    if (model.sellAndRent) cashflowDrivers.push('rent now has to be paid, with any Rent Assistance helping offset it');
    if (isBuyUp) cashflowDrivers.push(`the assets sold to fund the dearer home stop earning their income`);
    if (!model.sellAndRent && !isBuyUp && model.freedCapital > 0)
        cashflowDrivers.push(`the ${formatCurrency(model.freedCapital)} freed up starts earning where it is deployed`);
    if (pensionDelta !== 0)
        cashflowDrivers.push(
            `the Age Pension is ${pensionDelta > 0 ? 'higher' : 'lower'} (${formatCurrency(pensionBefore)} → ${formatCurrency(pensionAfter)} a year)`,
        );
    const cashflowWhy =
        `A full year of money in minus money out, on the same basis as the Financial Impact panel.` +
        (cashflowDrivers.length > 0 ? ` What changes it: ${cashflowDrivers.join('; ')}.` : '');

    /*
     * THE ESTATE, AND WHAT DECIDES IT. Both sides are projected over the same years from the same
     * engine — this move against simply not making it — so the comparison is like for like. The
     * reason names the three things that actually move it, and says plainly that the transaction
     * costs are gone for good, which is the part clients most often do not expect.
     */
    const legacyAfter = fin.legacyEstimate ?? null;
    const legacyDelta = baselineLegacy != null && legacyAfter != null ? Math.round(legacyAfter - baselineLegacy) : null;
    const legacyDrivers: EstateDriver[] = [];
    if (model.sellingCosts > 0) legacyDrivers.push({ text: 'Selling costs leave the estate on the day and never come back', amount: model.sellingCosts, sign: 'minus' });
    if (pensionDelta !== 0) legacyDrivers.push({ text: `${pensionDelta > 0 ? 'More' : 'Less'} Age Pension, compounding over the ${legacyYears} ${legacyYears === 1 ? 'year' : 'years'}`, amount: Math.abs(pensionDelta), suffix: 'a year', sign: pensionDelta > 0 ? 'plus' : 'minus' });
    if (!model.sellAndRent && model.newHomePrice > 0) legacyDrivers.push({ text: `The ${formatCurrency(model.newHomePrice)} home grows instead of the ${formatCurrency(model.salePrice)} one`, sign: 'neutral' });
    if (model.sellAndRent) legacyDrivers.push({ text: 'There is no longer a home growing in the background, and rent is paid from the savings', sign: 'minus' });
    if (model.freedCapital > 0) legacyDrivers.push({ text: 'The money freed up grows where it is deployed', amount: model.freedCapital, sign: 'plus' });
    const legacyWhy = (
        <WhatMovesIt
            lead={`Both figures are projected over the same ${legacyYears} ${legacyYears === 1 ? 'year' : 'years'} — this move against not downsizing — so they can be compared directly.`}
            drivers={legacyDrivers}
        />
    );

    const planPieces: PlanPiece[] = [];
    if (model.salePrice > 0) planPieces.push({ key: 'sell-home', name: 'Sell the family home', amount: model.salePrice, what: `The home is sold; after ${formatCurrency(model.sellingCosts)} of selling costs the money buys the next home and frees the rest. The home was exempt; whatever is not put into the next home becomes money Centrelink counts.` });
    if (!model.sellAndRent && model.newHomePrice > 0) planPieces.push({ key: 'new-home', name: 'Buy a smaller home', amount: model.newHomePrice, what: 'The new home is the principal residence, so it is exempt from the assets test while it is lived in. Stamp duty on the purchase is charged where a state was chosen.', glossaryTerm: 'home exemption cap' });
    if (model.sellAndRent) planPieces.push({ key: 'rent', name: 'Rent instead of buying', what: 'No new home is bought; rent is paid from the freed-up money. Without a home of your own, the whole amount is counted, and Rent Assistance may be payable.', glossaryTerm: 'rent assistance' });
    if (model.downsizerContribution > 0) planPieces.push({ key: 'downsizer', name: 'Downsizer contribution into super', amount: model.downsizerContribution, what: 'A special rule lets each person aged 55 or over put up to $300,000 from selling their home into super, on top of the usual limits. Inside super the earnings are taxed at no more than 15%.', glossaryTerm: 'downsizer contribution' });
    if (model.freedCapital > 0) planPieces.push({ key: 'freed', name: 'Money freed up', amount: model.freedCapital, what: 'What the sale leaves after the next home and its costs. Wherever it is kept or invested, it is counted in full as an asset and deemed to earn income.', glossaryTerm: 'deeming' });
    if (giftingAmount > 0) planPieces.push({ key: 'gift', name: 'Gift to family', amount: giftingAmount, what: 'Money given away. Up to $10,000 a year ($30,000 over five years) leaves the means tests straight away; anything above that is still counted for five years.', glossaryTerm: 'gifting' });


    return (
        <>
        <PlainWordsCard eyebrow="Your plan summary" className="mt-6">
            <div className="space-y-1.5 text-sm leading-relaxed text-slate-700">
                {isSeView && (
                    <p className="text-xs font-medium text-slate-500">
                        Narrating the selected Suggestion Engine approach: {model.name}.
                    </p>
                )}
                {model.sellAndRent ? (
                    <p>
                        Selling the {formatCurrency(model.salePrice)} home and renting frees up all of the equity — but as
                        a non-homeowner, Centrelink counts that money and <JargonTerm term="deems" >
                        {'Centrelink assumes financial assets earn a set rate of income (deeming) and counts that income in the pension income test, whatever they actually earn.'}
                        </JargonTerm> income on it. <JargonTerm term="Rent Assistance" /> may help with the rent.
                    </p>
                ) : isBuyUp ? (
                    <p>
                        Selling for {formatCurrency(model.salePrice)} and buying a dearer home for{' '}
                        {formatCurrency(model.newHomePrice)} moves{' '}
                        <span className="font-semibold text-slate-900">{formatCurrency(model.buyUpShortfall)}</span> of
                        countable savings into the new home — which Centrelink doesn't count at all.
                    </p>
                ) : (
                    <p>
                        Selling for {formatCurrency(model.salePrice)} and buying for {formatCurrency(model.newHomePrice)}{' '}
                        frees up <span className="font-semibold text-slate-900">{formatCurrency(model.freedCapital)}</span>.
                        {model.downsizerContribution > 0 && (
                            <>
                                {' '}{formatCurrency(model.downsizerContribution)} of it goes into super as a{' '}
                                <JargonTerm term="downsizer contribution" /> — Centrelink counts that from pension age.
                            </>
                        )}
                        {gift > 0 && <> {formatCurrency(gift)} is gifted.</>}
                    </p>
                )}
                {!model.sellAndRent && (
                    <p className="text-xs text-slate-500">
                        If there's a gap between selling and buying, the money set aside for the new home stays exempt for
                        up to 24 months — the <JargonTerm term="home sale proceeds exemption" />.
                    </p>
                )}
            </div>
            <div className="mt-3 border-t border-slate-100 pt-3">
                <ShiftRow variant="expanded" label="Age Pension" before={pensionBefore} after={pensionAfter} why={pensionWhy} />
                <ShiftRow variant="expanded" label="Yearly cashflow" before={cashflowBefore} after={cashflowAfter} why={cashflowWhy} />
                {legacyDelta != null && (
                    <ShiftRow
                        variant="expanded"
                        label={`Estate after ${legacyYears} ${legacyYears === 1 ? 'year' : 'years'} (vs not downsizing)`}
                        before={baselineLegacy as number}
                        after={legacyAfter as number}
                        suffix=""
                        why={legacyWhy}
                    />
                )}
            </div>
            <p className="mt-1 text-[11px] text-slate-400">
                For the Age Pension and cashflow, "before" is today, keeping the home; "after" matches the Financial
                Impact panel beside it. The estate compares two projections over the same years, not today&apos;s figure.
            </p>
            {giftExcess > 0 && (
                <p className="mt-2 rounded-lg border border-slate-200/70 border-l-4 border-l-amber-500 bg-white px-3 py-2 text-sm leading-relaxed text-amber-900 shadow-sm">
                    The {formatCurrency(giftExcess)} gifted above the yearly free area still counts for five years under
                    the <JargonTerm term="deprivation" /> rules.
                </p>
            )}
        </PlainWordsCard>
        <PlanPiecesCard className="mt-4" pieces={planPieces} />
        </>
    );
};

export default DownsizerPlanRecap;
